import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  FileText,
  Shield,
  Search,
  Clock,
  Calendar,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";
import { InnovriddhiMark } from "../components/InnovriddhiMark";
import "../styles/legal.css";

interface PolicyCard {
  to: string;
  title: string;
  icon: React.ReactNode;
  desc: string;
  read: string;
}

const POLICIES: PolicyCard[] = [
  {
    to: "/privacy-policy",
    title: "Privacy Policy",
    icon: <ShieldCheck className="w-6 h-6" />,
    desc: "What personal information Innovriddhi collects, how we use and store it, and the choices you have over your data.",
    read: "8 min read",
  },
  {
    to: "/terms-of-use",
    title: "Terms of Use",
    icon: <FileText className="w-6 h-6" />,
    desc: "The terms governing your access to and use of the Innovriddhi platform, applications and related services.",
    read: "12 min read",
  },
  {
    to: "/acceptable-use-policy",
    title: "Acceptable Use Policy",
    icon: <Shield className="w-6 h-6" />,
    desc: "What is and isn't allowed when using our platform: the rules that keep services safe and fair for everyone.",
    read: "5 min read",
  },
];

export const Legal: React.FC = () => {
  const [q, setQ] = useState("");
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return POLICIES;
    return POLICIES.filter(
      (p) =>
        p.title.toLowerCase().includes(needle) ||
        p.desc.toLowerCase().includes(needle),
    );
  }, [q]);

  return (
    <div className="lc selection:bg-plum selection:text-white">
      <header className="lc__bar">
        <Link
          to="/"
          className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-plum rounded-md"
        >
          <div className="text-plum group-hover:scale-105 transition-transform duration-200">
            <InnovriddhiMark size={34} form="interlaced" />
          </div>
          <span className="font-brand text-2xl font-bold tracking-tight text-ink">
            Innovriddhi
          </span>
        </Link>
        <Link className="lc__back" to="/">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </header>

      <main className="lc__main">
        <div>
          <span className="lc__eyebrow">Legal</span>
          <h1 className="lc__title">Legal centre</h1>
          <p className="lc__lede">
            Everything that governs your use of Innovriddhi. Read, search or
            open any policy.
          </p>
          <div className="lc__search">
            <Search className="w-4 h-4 text-ink-muted shrink-0" />
            <input
              placeholder="Search policies"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search policies"
            />
          </div>
          <div className="lc__meta">
            <div className="lc__metarow">
              <span className="lc__mic">
                <Clock className="w-4 h-4" />
              </span>
              <div>
                <b>Last reviewed</b>
                <span>15 Jul 2026</span>
              </div>
            </div>
          </div>
        </div>

        <div className="lc__cards">
          {shown.map((d) => (
            <Link className="lc__card group" to={d.to} key={d.to}>
              <span className="lc__chip">{d.icon}</span>
              <span>
                <div className="lc__cardtitle">{d.title}</div>
                <div className="lc__carddesc">{d.desc}</div>
                <div className="lc__cardmeta">
                  <span>
                    <Clock className="w-3.5 h-3.5" />
                    {d.read}
                  </span>
                  <span>
                    <Calendar className="w-3.5 h-3.5" />
                    Updated 15 Jul 2026
                  </span>
                </div>
              </span>
              <span className="lc__arrow">
                <ArrowRight className="w-5 h-5" />
              </span>
            </Link>
          ))}
          {shown.length === 0 && (
            <div className="lc__empty">
              No policies match &ldquo;{q}&rdquo;.
            </div>
          )}
        </div>
      </main>

      <footer className="lc__foot">
        <span>
          &copy; {new Date().getFullYear()} Innovriddhi Technologies Private
          Limited &middot; innovriddhi.com
        </span>
        <a href="mailto:info@innovriddhi.com">info@innovriddhi.com</a>
      </footer>
    </div>
  );
};

export default Legal;
